import { useEffect, useRef, useState, Fragment } from "react";

export const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Magnetic hover wrapper */
export function Magnetic({ children, className = "", strength = 0.25 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion() || !window.matchMedia("(pointer: fine)").matches) return;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      el.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * strength}px, ${(e.clientY - r.top - r.height / 2) * (strength + 0.08)}px)`;
    };
    const leave = () => { el.style.transform = ""; };
    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    return () => { el.removeEventListener("mousemove", move); el.removeEventListener("mouseleave", leave); };
  }, [strength]);
  return <span ref={ref} className={`magnetic ${className}`}>{children}</span>;
}

/* In-view reveal (wrap anything) */
export function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => { if (e.isIntersecting) { el.classList.add("in"); io.disconnect(); } }),
      { threshold: 0.18 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

/* Word-by-word slide-up heading. parts: string or [{t, em}] */
export function W2({ parts, as: Tag = "h2", className = "" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }),
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const segs = typeof parts === "string" ? [{ t: parts }] : parts;
  let k = 0;
  return (
    <Tag ref={ref} className={`w2 ${inView ? "in" : ""} ${className}`}>
      {segs
        .flatMap((s, si) => s.t.split(" ").filter(Boolean).map((w, wi) => ({ w, em: s.em, key: `${si}-${wi}` })))
        .map(({ w, em, key }, i, all) => {
          const d = k++ * 70;
          // The space sits outside the inline-block word so it never collapses
          // (keeps "Six lines." readable for screen readers and Google too).
          return (
            <Fragment key={key}>
              <span className="w2-w" style={{ transitionDelay: `${d}ms` }}>
                <span className={em ? "w2-i em" : "w2-i"}>{w}</span>
              </span>
              {i < all.length - 1 ? " " : null}
            </Fragment>
          );
        })}
    </Tag>
  );
}

/* Count-up on scroll */
export function Counter({ value, format, className = "" }) {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        if (reducedMotion()) { setN(value); return; }
        const t0 = performance.now();
        const step = (t) => {
          const p = Math.min(1, (t - t0) / 1400);
          setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }),
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);
  const txt = format ? format(n) : n.toLocaleString("en-IN");
  return <span ref={ref} className={`counter ${className}`}>{txt}</span>;
}

/* Clip-path image reveal on scroll */
export function ImgReveal({ src, alt, className = "", children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => { if (e.isIntersecting) { el.classList.add("in"); io.disconnect(); } }),
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`imgreveal ${className}`}>
      <img src={src} alt={alt} loading="lazy" />
      {children}
    </div>
  );
}
